<template>
  <div id="app-root">
    <!-- 顶栏 -->
    <header class="header">
      <div class="header-inner">
        <button v-if="showBackLogo" type="button" class="logo logo-back" @click="goBack" aria-label="返回上一页" title="返回上一页">
          <span class="logo-back-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
          </span>
          <span class="logo-mark">
            <img v-if="ui.currentTopic && ui.currentTopic.icon" :data-imagebed-src="ui.currentTopic.icon" class="logo-topic-icon" alt="" />
            <img v-else :src="siteLogo" class="logo-site-icon" alt="中文方舟" />
          </span>
          <span class="logo-text">{{ ui.currentTopic ? ui.currentTopic.title : '中文方舟' }}</span>
        </button>
        <button v-else type="button" class="logo logo-home-trigger" @click="openHomeHub" aria-label="打开首页与最近访问" title="打开首页与最近访问">
          <span class="logo-mark">
            <img v-if="ui.currentTopic && ui.currentTopic.icon" :data-imagebed-src="ui.currentTopic.icon" class="logo-topic-icon" alt="" />
            <img v-else :src="siteLogo" class="logo-site-icon" alt="中文方舟" />
          </span>
          <span class="logo-text">{{ ui.currentTopic ? ui.currentTopic.title : '中文方舟' }}</span>
        </button>

        <button type="button" class="search-box" :aria-label="searchPlaceholder" @click="goSearch">
          <svg class="sb-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <span class="sb-placeholder">{{ searchPlaceholder }}</span>
        </button>

        <div class="region-badge" title="当前访客区域">
          {{ isCn ? '🇨🇳 境内' : '🌏 海外' }}
        </div>

        <div class="header-actions">
          <button class="icon-btn theme-toggle"
                  @click="ui.toggleTheme()"
                  :title="ui.theme === 'dark' ? '切换到日间模式' : '切换到夜间模式'"
                  :aria-label="ui.theme === 'dark' ? '切换到日间模式' : '切换到夜间模式'">
            <!-- 夜间显示太阳（点击→日间）；日间显示月亮（点击→夜间） -->
            <svg v-if="ui.theme === 'dark'" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="4.2"/><path d="M12 2v2.6M12 19.4V22M4.22 4.22l1.84 1.84M17.94 17.94l1.84 1.84M2 12h2.6M19.4 12H22M4.22 19.78l1.84-1.84M17.94 6.06l1.84-1.84"/></svg>
            <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
          </button>

          <button class="btn-primary label" @click="onPost">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            <span>{{ ui.currentTopic ? '发帖到此话题' : '发帖' }}</span>
          </button>

          <router-link v-if="userStore.isLogin" to="/me#messages" class="icon-btn notify" title="消息">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
            <span v-if="userStore.unread > 0" class="badge-num">{{ userStore.unread > 99 ? '99+' : userStore.unread }}</span>
          </router-link>

          <button v-if="userStore.isLogin" class="icon-btn dm" title="私信" aria-label="私信" @click="ui.openDmModal()">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
            <span v-if="userStore.dmUnread > 0" class="badge-num">{{ userStore.dmUnread > 99 ? '99+' : userStore.dmUnread }}</span>
          </button>

          <router-link v-if="userStore.isLogin" to="/me" class="avatar-link">
            <img class="avatar" :data-imagebed-src="myAvatar" alt="我" />
          </router-link>
          <router-link v-else to="/login" class="btn-ghost">登录</router-link>
        </div>
      </div>
    </header>

    <!-- 未绑定邮箱提示条（匿名用户） -->
    <div v-if="showEmailTip" class="email-tip-bar">
      <span class="et-icon">✉️</span>
      <span class="et-text">你正在使用匿名身份，未绑定邮箱。清理浏览器数据会导致账号丢失——绑定邮箱即可跨设备登录。</span>
      <button class="btn-primary et-btn" @click="openEmailBind">立即绑定</button>
      <button class="et-close" type="button" @click="dismissEmailTip" title="不再提示" aria-label="不再提示">✕</button>
    </div>

    <!-- 缓存话题页与搜索页：从详情页后退时组件不重建，列表/滚动/下钻/AI标注等状态原样保留 -->
    <router-view v-slot="{ Component }">
      <keep-alive :include="['TopicView', 'SearchView']">
        <component :is="Component" />
      </keep-alive>
    </router-view>

    <!-- 移动端底栏 -->
    <nav class="mobile-nav">
      <button type="button" class="nav-item nav-home-btn" :class="{ active: $route.name === 'home' }" @click="openHomeHub" aria-label="打开首页与最近访问">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
        首页
      </button>
      <router-link class="nav-item" to="/about" active-class="active">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
        关于
      </router-link>
      <button class="nav-item fab" @click="onPost" aria-label="发帖">
        <span class="fab-circle">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        </span>
      </button>
      <router-link class="nav-item" to="/me#messages" active-class="active">
        <span class="nav-ic">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
          <span v-if="userStore.unread > 0" class="badge-num">{{ userStore.unread > 99 ? '99+' : userStore.unread }}</span>
        </span>
        消息
      </router-link>
      <router-link class="nav-item" to="/me" active-class="active">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
        我的
      </router-link>
    </nav>

    <!-- 全局发帖浮层 -->
    <PostComposer />

    <!-- 邮箱绑定浮层 -->
    <EmailBindModal :visible="showEmailBind" @update:visible="showEmailBind = $event" />

    <!-- 用户资料浮动卡（点头像触发） -->
    <UserProfileCard />
    <!-- 私信全局浮层 -->
    <DmModal />

    <transition name="home-hub">
      <div v-if="homeHubOpen" class="home-hub-mask" @click.self="closeHomeHub">
        <div class="home-hub" role="dialog" aria-modal="true" aria-label="首页与最近访问">
          <div class="home-hub-head">
            <div>
              <div class="home-hub-eyebrow">快速切换</div>
              <div class="home-hub-title">首页与最近访问</div>
            </div>
            <button type="button" class="home-hub-close" @click="closeHomeHub" aria-label="关闭">✕</button>
          </div>

          <div class="home-hub-section">
            <div class="home-hub-section-title">快速入口</div>
            <div class="home-topic-grid">
              <button type="button" class="home-topic-card" @click="goHome">
                <span class="home-topic-icon home-topic-icon-primary">
                  <img :src="siteLogo" class="home-topic-img" alt="中文方舟" />
                </span>
                <span class="home-topic-meta">
                  <span class="home-topic-text">主论坛程序</span>
                  <span class="home-topic-subtext">主论坛</span>
                </span>
              </button>

              <template v-if="recentTopics.length">
                <button
                  v-for="topic in recentTopics"
                  :key="topic.id"
                  type="button"
                  class="home-topic-card"
                  @click="goTopic(topic)"
                >
                  <span class="home-topic-icon">
                    <img v-if="topic.icon" :data-imagebed-src="topic.icon" class="home-topic-img" alt="" />
                    <span
                      v-else
                      class="home-topic-fallback"
                      :style="topicFallbackStyle(topic.title)"
                    >{{ topicInitial(topic.title) }}</span>
                  </span>
                  <span class="home-topic-meta">
                    <span class="home-topic-text">{{ topic.title }}</span>
                    <span class="home-topic-subtext">最近访问</span>
                  </span>
                </button>
              </template>
            </div>
            <div v-if="!recentTopics.length" class="home-hub-empty">最近还没有访问记录，去逛逛主论坛吧。</div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Toast -->
    <transition name="toast">
      <div v-if="ui.toast" class="toast" :class="ui.toast.type">{{ ui.toast.msg }}</div>
    </transition>
  </div>
</template>

<script>
import { useUserStore } from '@/store/user'
import { useUiStore } from '@/store/ui'
import PostComposer from '@/components/PostComposer.vue'
import EmailBindModal from '@/components/EmailBindModal.vue'
import UserProfileCard from '@/components/UserProfileCard.vue'
import DmModal from '@/components/DmModal.vue'
import { useHistoryStore } from '@/store/history'
import { defaultAvatar } from '@/utils/avatar'
import { getCachedSite, getSiteData } from '@/utils/site'
import { getIsCn, probeRegion, onRegionChange } from '@/utils/region'
import siteLogo from '@/assets/logo.svg'

const topicFallbackPalettes = [
  ['#f97316', '#fb7185'],
  ['#14b8a6', '#22c55e'],
  ['#3b82f6', '#6366f1'],
  ['#8b5cf6', '#ec4899'],
  ['#eab308', '#f97316'],
  ['#06b6d4', '#3b82f6'],
]

export default {
  name: 'App',
  components: { PostComposer, EmailBindModal, UserProfileCard, DmModal },
  data() {
    return {
      userStore: useUserStore(),
      ui: useUiStore(),
      historyStore: useHistoryStore(),
      site: getCachedSite() || {},
      isCn: getIsCn(),          // 客户端探测的境内/海外标识，徽章绑定此值
      siteLogo,
      showEmailBind: false,
      emailTipDismissed: false,   // 已登录匿名用户主动隐藏未绑定邮箱提示条（按 uid 记忆）
      homeHubOpen: false,
    }
  },
  computed: {
    myAvatar() {
      const u = this.userStore.user
      return u?.avatar || defaultAvatar(u?.username, u?.id)
    },
    showBackLogo() {
      return this.$route.name === 'post' && !!window.history.state?.back
    },
    searchPlaceholder() {
      return this.ui.currentTopic ? '在此话题下搜索…' : '搜索话题、帖子…'
    },
    // 仅在已登录、未绑定邮箱、且未主动隐藏时显示提示条
    showEmailTip() {
      return this.userStore.isLogin && !this.userStore.hasEmail && !this.emailTipDismissed
    },
    recentTopics() {
      return this.historyStore.visited.slice(0, 6)
    },
  },
  watch: {
    // 登录态 / 用户切换后同步「提示条是否已隐藏」
    'userStore.user'(u) { this.emailTipDismissed = this.loadEmailTipDismissed(u) },
    homeHubOpen(open) {
      if (typeof document === 'undefined') return
      if (open) {
        this._onHomeHubEsc = (e) => { if (e.key === 'Escape') this.closeHomeHub() }
        document.addEventListener('keydown', this._onHomeHubEsc)
      } else if (this._onHomeHubEsc) {
        document.removeEventListener('keydown', this._onHomeHubEsc)
        this._onHomeHubEsc = null
      }
    },
  },
  created() {
    // 尽早启动外网探测（fire-and-forget）；is_cn 由 region.js 统一管理
    probeRegion()
    // 探测完成后实时刷新徽章
    this._unsubRegion = onRegionChange((v) => { this.isCn = v })
  },
  async mounted() {
    this.ui.initTheme()
    // 站点其它字段（name/intro/site_open/site_notice）后台刷新，不阻塞首屏；
    // is_cn 已由 region.js 提供，不再依赖此接口
    getSiteData().then((s) => { this.site = s }).catch(() => { /* ignore */ })
    if (this.userStore.token) {
      await this.userStore.fetchMe()
      this.emailTipDismissed = this.loadEmailTipDismissed(this.userStore.user)
      this.userStore.fetchUnread()
      this.userStore.fetchDmUnread()
      this.userStore.maybeRefreshByFk()   // 建立 user_center fk 基线，后续变化才重拉 liked
    }
  },
  beforeUnmount() {
    if (this._unsubRegion) { this._unsubRegion(); this._unsubRegion = null }
    if (this._onHomeHubEsc) {
      document.removeEventListener('keydown', this._onHomeHubEsc)
      this._onHomeHubEsc = null
    }
  },
  methods: {
    emailTipKey(u) {
      return u && u.id ? 'crirp-email-tip-hidden:' + u.id : ''
    },
    loadEmailTipDismissed(u) {
      const k = this.emailTipKey(u)
      return k ? localStorage.getItem(k) === '1' : false
    },
    openEmailBind() {
      this.showEmailBind = true
    },
    dismissEmailTip() {
      const k = this.emailTipKey(this.userStore.user)
      if (k) localStorage.setItem(k, '1')
      this.emailTipDismissed = true
    },
    openHomeHub() {
      this.homeHubOpen = true
    },
    closeHomeHub() {
      this.homeHubOpen = false
    },
    topicInitial(title) {
      const text = String(title || '').trim()
      return text ? Array.from(text)[0] : '话'
    },
    topicFallbackStyle(title) {
      const text = String(title || '')
      let hash = 0
      for (const ch of text) hash = ((hash << 5) - hash) + ch.codePointAt(0)
      const [from, to] = topicFallbackPalettes[Math.abs(hash) % topicFallbackPalettes.length]
      return {
        background: `linear-gradient(145deg, ${from}, ${to})`,
      }
    },
    goHome() {
      this.closeHomeHub()
      if (this.$route.name !== 'home') this.$router.push('/')
    },
    goTopic(topic) {
      if (!topic) return
      this.closeHomeHub()
      this.$router.push('/t/' + (topic.slug || topic.id))
    },
    goBack() {
      if (this.showBackLogo) {
        this.$router.back()
        return
      }
      this.$router.push('/')
    },
    onPost() {
      if (!this.userStore.isLogin) {
        this.$router.push('/login')
        return
      }
      // 优先用话题页当前下钻选中的子话题，否则用母话题
      const n = this.ui.currentNode
      const t = this.ui.currentTopic
      if (n) {
        this.ui.openComposer(n.id, n.title)
      } else if (t) {
        this.ui.openComposer(t.id, t.title)
      } else {
        this.ui.openComposer()
      }
    },
    goSearch() {
      const t = this.ui.currentTopic
      // 点击式入口：不带 kw，搜索逻辑全在 /search 页内完成；话题页带上话题上下文
      const query = t ? { topic_id: t.id, topic_title: t.title } : {}
      this.$router.push({ name: 'search', query })
    },
  },
}
</script>

<style scoped>
.header { position: sticky; top: 0; z-index: 50; background: var(--surface); border-bottom: 1px solid var(--border); }
.header-inner { max-width: var(--maxw); margin: 0 auto; padding: 0 20px; height: 64px; display: flex; align-items: center; gap: 16px; }
.logo { display: flex; align-items: center; gap: 9px; font-weight: 800; font-size: 18px; letter-spacing: -.3px; white-space: nowrap; }
.logo-home-trigger { padding: 0; border: none; background: none; color: inherit; cursor: pointer; text-align: left; }
.logo-back { padding: 0; border: none; background: none; color: inherit; cursor: pointer; }
.logo-back-icon {
  width: 24px;
  height: 24px;
  display: grid;
  place-items: center;
  color: var(--text-2);
  flex-shrink: 0;
}
.logo-back-icon svg { width: 18px; height: 18px; }
.logo-mark { width: 32px; height: 32px; border-radius: 9px; display: grid; place-items: center; box-shadow: 0 4px 12px rgba(79,70,229,.35); overflow: hidden; }
.logo-site-icon { width: 100%; height: 100%; display: block; }
.logo-topic-icon { width: 100%; height: 100%; object-fit: cover; border-radius: 9px; }
.region-badge { font-size: 12px; color: var(--text-2); background: var(--surface-2); border: 1px solid var(--border); padding: 4px 10px; border-radius: 999px; white-space: nowrap; }
.search-box {
  flex: 1 1 0; min-width: 0; max-width: 420px; height: 40px; padding: 0 14px 0 38px;
  position: relative; display: flex; align-items: center; text-align: left;
  border: 1px solid var(--border-strong); border-radius: 999px;
  background: var(--surface-2); font: inherit; cursor: pointer; outline: none;
  transition: border-color .15s, background .15s, box-shadow .15s;
}
.search-box:hover { background: var(--surface); border-color: var(--primary); box-shadow: 0 0 0 3px var(--primary-soft); }
.sb-icon { position: absolute; left: 12px; width: 17px; height: 17px; color: var(--text-3); pointer-events: none; }
.sb-placeholder { font-size: 14px; color: var(--text-3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.header-actions { display: flex; align-items: center; gap: 10px; margin-left: auto; flex-shrink: 0; }
.badge-num {
  position: absolute; top: 3px; right: 3px;
  min-width: 17px; height: 17px; padding: 0 4px;
  background: var(--danger); color: #fff;
  border: 2px solid var(--surface); border-radius: 999px;
  font-size: 10px; font-weight: 700; line-height: 13px; text-align: center;
  box-sizing: border-box; z-index: 2;
}
.avatar-link { display: grid; place-items: center; flex-shrink: 0; }

/* 未绑定邮箱提示条 */
.email-tip-bar {
  max-width: var(--maxw); margin: 0 auto; padding: 9px 20px;
  display: flex; align-items: center; gap: 12px;
  background: var(--warn-bg); border-bottom: 1px solid var(--warn-border);
  color: var(--warn-fg); font-size: 13px;
}
.et-icon { font-size: 15px; flex-shrink: 0; }
.et-text { flex: 1; line-height: 1.5; min-width: 0; }
.et-btn { height: 32px; padding: 0 14px; font-size: 13px; flex-shrink: 0; }
.et-close {
  flex-shrink: 0; width: 26px; height: 26px; border-radius: 6px;
  border: none; background: none; color: var(--warn-fg); cursor: pointer;
  font-size: 13px; opacity: .65; transition: background .15s, opacity .15s;
}
.et-close:hover { opacity: 1; background: rgba(0,0,0,.07); }

.mobile-nav { display: none; }
.nav-home-btn { border: none; background: none; padding: 0; cursor: pointer; font: inherit; }

.home-hub-mask {
  position: fixed; inset: 0; z-index: 140;
  display: flex; align-items: flex-end; justify-content: center;
  padding: 20px 16px calc(88px + env(safe-area-inset-bottom));
  background: rgba(15, 23, 42, .42);
  backdrop-filter: blur(10px);
}
.home-hub {
  width: min(100%, 420px);
  padding: 20px;
  border-radius: 28px;
  background:
    radial-gradient(circle at top left, rgba(244, 114, 182, .12), transparent 34%),
    radial-gradient(circle at top right, rgba(59, 130, 246, .14), transparent 32%),
    linear-gradient(180deg, rgba(255,255,255,.98), rgba(248,250,252,.98));
  border: 1px solid rgba(148, 163, 184, .2);
  box-shadow: 0 26px 70px rgba(15, 23, 42, .22);
}
.home-hub-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 16px; }
.home-hub-eyebrow { font-size: 12px; font-weight: 700; letter-spacing: .08em; color: var(--text-3); text-transform: uppercase; }
.home-hub-title { margin-top: 4px; font-size: 22px; font-weight: 800; color: var(--text); }
.home-hub-close {
  width: 34px; height: 34px; border: none; border-radius: 12px;
  background: rgba(255,255,255,.78); color: var(--text-3); cursor: pointer;
  font-size: 15px; transition: background .15s, color .15s, transform .15s;
}
.home-hub-close:hover { background: var(--surface); color: var(--text); transform: translateY(-1px); }
.home-hub-section { margin-top: 18px; }
.home-hub-section-title { margin-bottom: 12px; font-size: 14px; font-weight: 700; color: var(--text-2); }
.home-topic-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px 8px; }
.home-topic-card {
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  min-height: 0; padding: 0;
  border: none;
  background: transparent;
  text-align: center; cursor: pointer;
  transition: transform .15s ease, opacity .15s ease;
}
.home-topic-card:hover {
  transform: translateY(-2px);
}
.home-topic-icon {
  width: 56px; height: 56px; border-radius: 18px; overflow: hidden; flex-shrink: 0;
  display: grid; place-items: center;
  background: linear-gradient(145deg, rgba(255,255,255,.95), rgba(241,245,249,.92));
  border: 1px solid rgba(148, 163, 184, .24);
  box-shadow:
    0 8px 18px rgba(15, 23, 42, .08),
    inset 0 1px 0 rgba(255,255,255,.88);
}
.home-topic-icon-primary {
  background: linear-gradient(145deg, rgba(79, 70, 229, .14), rgba(244, 114, 182, .12));
  border-color: rgba(99, 102, 241, .26);
  box-shadow:
    0 10px 22px rgba(79, 70, 229, .16),
    inset 0 1px 0 rgba(255,255,255,.82);
}
.home-topic-meta { min-width: 0; display: flex; flex-direction: column; gap: 2px; width: 100%; align-items: center; }
.home-topic-img { width: 100%; height: 100%; object-fit: cover; display: block; }
.home-topic-fallback {
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  color: #fff;
  font-size: 22px;
  font-weight: 800;
  line-height: 1;
  user-select: none;
}
.home-topic-text {
  max-width: 100%;
  font-size: 11.5px; font-weight: 700; color: var(--text);
  line-height: 1.35;
  display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; overflow: hidden;
}
.home-topic-subtext {
  font-size: 10px;
  line-height: 1.3;
  color: var(--text-3);
}
.home-hub-empty {
  padding: 14px 16px;
  border-radius: 18px;
  background: rgba(255,255,255,.74);
  border: 1px dashed var(--border);
  font-size: 13px; line-height: 1.7; color: var(--text-3);
}
.home-hub-enter-active, .home-hub-leave-active { transition: opacity .2s ease; }
.home-hub-enter-active .home-hub, .home-hub-leave-active .home-hub { transition: transform .22s ease, opacity .22s ease; }
.home-hub-enter-from, .home-hub-leave-to { opacity: 0; }
.home-hub-enter-from .home-hub, .home-hub-leave-to .home-hub { transform: translateY(16px) scale(.98); opacity: 0; }

.toast {
  position: fixed; left: 50%; bottom: 80px; transform: translateX(-50%);
  z-index: 200; padding: 10px 18px; border-radius: 999px; font-size: 14px;
  background: var(--invert-bg); color: var(--invert-fg); box-shadow: var(--shadow-lg); max-width: 90vw;
}
.toast.success { background: #16a34a; color: #fff; }
.toast.error { background: var(--danger); color: #fff; }
.toast-enter-active, .toast-leave-active { transition: opacity .2s; }
.toast-enter-from, .toast-leave-to { opacity: 0; }

@media (max-width: 640px) {
  .header-inner { height: 56px; padding: 0 14px; gap: 10px; }
  .logo-text { display: none; }
  .region-badge { display: none; }
  .email-tip-bar { padding: 9px 14px; gap: 8px; }
  .et-text { font-size: 12px; }
  .search-box { max-width: none; height: 38px; }
  .btn-primary.label { display: none; }
  .icon-btn.notify { display: none; }
  .home-hub-mask { align-items: flex-end; padding: 16px 12px calc(82px + env(safe-area-inset-bottom)); }
  .home-hub { width: 100%; padding: 18px 16px; border-radius: 24px; }
  .home-hub-title { font-size: 20px; }
  .home-topic-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px 6px; }
  .home-topic-icon { width: 52px; height: 52px; border-radius: 17px; }
  .home-topic-fallback { font-size: 20px; }
  .home-topic-text { font-size: 11px; }
  .home-topic-subtext { font-size: 10px; }
  .nav-ic { position: relative; display: inline-flex; }
  .nav-ic .badge-num { top: -5px; right: -10px; }
  .mobile-nav {
    display: flex; position: fixed; bottom: 0; left: 0; right: 0; height: 62px;
    background: var(--surface); border-top: 1px solid var(--border); z-index: 60;
    padding-bottom: env(safe-area-inset-bottom);
  }
  .nav-item { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 3px; color: var(--text-3); font-size: 11px; position: relative; }
  .nav-item.active { color: var(--primary); }
  .nav-item svg { width: 22px; height: 22px; }
  .nav-item.fab { flex: 0 0 auto; width: 56px; margin-top: -18px; }
  .fab-circle { width: 48px; height: 48px; border-radius: 50%; background: linear-gradient(135deg, var(--primary), #7c83ff); color: #fff; display: grid; place-items: center; box-shadow: 0 6px 16px rgba(79,70,229,.42); }
  .fab-circle svg { width: 24px; height: 24px; }
}

@media (min-width: 641px) {
  .home-topic-grid { grid-template-columns: repeat(5, minmax(0, 1fr)); }
}
</style>
